import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const currentYear = new Date().getFullYear();
  const [partners, setPartners] = useState([]);
  // Load from cache first for instant display
  const [siteSettings, setSiteSettings] = useState(() => {
    try {
      const cached = localStorage.getItem('site_settings');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [footerSettings, setFooterSettings] = useState({
    description_en: "Specialist in industrial and agricultural spare parts and equipment since 1998.",
    description_fr: "Spécialiste du matériel et de la pièce de rechange industriels et agricoles depuis 1998.",
    description_ar: "متخصصون في المعدات وقطع الغيار الزراعية والصناعية منذ 1998.",
    address: "USTO - ORAN, Algeria",
    phone: "+213 (41) 706 906",
    email: "contact@sae-dz.com",
    hours_weekdays_en: "Sun - Thu",
    hours_weekdays_fr: "Dim - Jeu",
    hours_weekdays_ar: "الأحد - الخميس",
    hours_weekdays_time: "8:00 - 17:00",
    hours_weekend_en: "Fri - Sat",
    hours_weekend_fr: "Ven - Sam",
    hours_weekend_ar: "الجمعة - السبت",
    hours_weekend_status_en: "Closed",
    hours_weekend_status_fr: "Fermé",
    hours_weekend_status_ar: "مغلق"
  });

  useEffect(() => {
    // Fetch partners
    fetch(`${BACKEND_URL}/api/partners`)
      .then(res => res.json())
      .then(data => {
        if (data && data.length > 0) {
          setPartners(data.slice(0, 6));
        }
      })
      .catch(err => console.error('Error fetching partners:', err));

    // Fetch footer settings
    fetch(`${BACKEND_URL}/api/footer-settings`)
      .then(res => res.json())
      .then(data => {
        if (data) {
          setFooterSettings(data);
        }
      })
      .catch(err => console.error('Error fetching footer settings:', err));
      
    // Fetch site settings (logo) and cache
    fetch(`${BACKEND_URL}/api/site-settings`)
      .then(res => res.json())
      .then(data => {
        setSiteSettings(data);
        try { localStorage.setItem('site_settings', JSON.stringify(data)); } catch {}
      })
      .catch(() => {});
  }, []);

  // Scroll to top on navigation
  const handleNavClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-slate-950 text-white">
      {/* Partners strip */}
      <div className="border-b border-slate-800">
        <div className="container mx-auto px-6 py-12">
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-8 text-center">
            {language === 'ar' ? 'الممثل الرسمي لـ' : language === 'en' ? 'Official Representative of' : 'Représentant Officiel de'}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-12">
            {partners.map((partner) => (
              <Link
                key={partner.id}
                to={`/partner/${partner.id}`}
                onClick={handleNavClick}
                className="opacity-50 hover:opacity-100 transition-opacity duration-300"
              >
                <img 
                  src={partner.logo} 
                  alt={partner.name} 
                  className="h-8 w-auto"
                />
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Main footer content */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            {(siteSettings?.logo_footer || siteSettings?.logo_header) && (
              <img 
                src={siteSettings.logo_footer || siteSettings.logo_header} 
                alt={siteSettings.logo_alt || "SAE"} 
                className="h-14 w-auto mb-6 object-contain"
              />
            )}
            <p className="text-slate-400 text-sm leading-relaxed">
              {language === 'ar' 
                ? (footerSettings.description_ar || footerSettings.description_fr)
                : language === 'en' 
                  ? footerSettings.description_en 
                  : footerSettings.description_fr}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs uppercase tracking-widest text-slate-500 mb-6">
              {language === 'ar' ? 'التنقل' : language === 'en' ? 'Navigation' : 'Navigation'}
            </h4>
            <ul className="space-y-3">
              {[t('nav.equipment'), t('nav.services'), t('nav.partners'), t('nav.references'), t('nav.contact')].map((item, idx) => (
                <li key={idx}>
                  <Link 
                    to={`/${['equipment', 'services', 'partners', 'references', 'contact'][idx]}`} 
                    onClick={handleNavClick}
                    className="text-slate-400 hover:text-white transition-colors text-sm flex items-center gap-1 group"
                  >
                    {item}
                    <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-xs uppercase tracking-widest text-slate-500 mb-6">
              {language === 'ar' ? 'اتصل بنا' : language === 'en' ? 'Contact' : 'Contact'}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-slate-400">
                <MapPin size={16} className="text-slate-500 flex-shrink-0 mt-1" />
                <a 
                  href="https://maps.app.goo.gl/GPaMHSSiUcEEy97D6" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  {footerSettings.address}
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm text-slate-400">
                <Phone size={16} className="text-slate-500 flex-shrink-0" />
                <a href={`tel:${footerSettings.phone?.replace(/\s/g, '')}`} className="hover:text-white transition-colors">
                  {footerSettings.phone}
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm text-slate-400">
                <Mail size={16} className="text-slate-500 flex-shrink-0" />
                <a href={`mailto:${footerSettings.email}`} className="hover:text-white transition-colors">
                  {footerSettings.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="text-xs uppercase tracking-widest text-slate-500 mb-6">
              {language === 'ar' ? 'ساعات العمل' : language === 'en' ? 'Hours' : 'Horaires'}
            </h4>
            <div className="text-sm text-slate-400 space-y-2">
              <p>
                <span className="text-white">
                  {language === 'ar' 
                    ? (footerSettings.hours_weekdays_ar || footerSettings.hours_weekdays_fr)
                    : language === 'en' 
                      ? footerSettings.hours_weekdays_en 
                      : footerSettings.hours_weekdays_fr}
                </span><br />
                {footerSettings.hours_weekdays_time}
              </p>
              <p>
                <span className="text-white">
                  {language === 'ar' 
                    ? (footerSettings.hours_weekend_ar || footerSettings.hours_weekend_fr)
                    : language === 'en' 
                      ? footerSettings.hours_weekend_en 
                      : footerSettings.hours_weekend_fr}
                </span><br />
                {language === 'ar' 
                  ? (footerSettings.hours_weekend_status_ar || footerSettings.hours_weekend_status_fr)
                  : language === 'en' 
                    ? footerSettings.hours_weekend_status_en 
                    : footerSettings.hours_weekend_status_fr}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-slate-800">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-500">
              © {currentYear} SAE Agro Equipment. {t('footer.rights')}.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
